import type { Cell, CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import BillingTypeIndicator from 'af-root/scenes/Company/FieldReports/FieldReportsList/FieldReport/WorkSummary/BillingTypeIndicator';

import type { W_Job_JobTrackingEntries_VM_Row, W_Job_JobTrackingEntries_VM_CrewType, W_Job_JobTrackingEntries_VM_MaterialHeader, W_Job_JobTrackingEntries_VM_WorkOrder } from 'ab-api/web/job/findJobTrackingEntriesTable';
import type { W_Job_JobTrackingSyncStatus_VM } from 'ab-api/web/job/findJobTrackingSyncStatus';
import type { W_Job_JobTrackingTemplateColumn_VM } from 'ab-api/web/job/findJobTrackingTemplateInformationColumns';
import type { W_Job_JobTrackingTotals_VM_Totals } from 'ab-api/web/job/findJobTrackingTotals';

import type { BillingCodeVM } from 'ab-viewModels/jobPreview.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { convertUnits } from '@acceligentllc/shared/utils/unitConversion';

import { JobTrackingSyncQueueStatus } from '@acceligentllc/shared/enums/jobTrackingSyncQueue';
import { JobTrackingTemplateColumnType } from '@acceligentllc/shared/enums/jobTrackingTemplateColumnType';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { QuantityUnitMap } from '@acceligentllc/shared/enums/quantityUnit';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import type { RootState } from 'af-reducers';

import { debounce } from 'af-utils/actions.util';
import { bemElement } from 'af-utils/bem.util';
import { dollarUnitFormatter } from 'af-utils/format.util';
import { getFullClientUrl } from 'af-utils/http.util';
import * as SettingsUtils from 'af-utils/settings.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import MaterialCostSource from 'ab-enums/materialCostSource.enum';
import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import * as SettingsKeys from 'af-constants/settingsKeys';

import { SEARCH_DELAY } from 'ab-common/constants/value';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { SearchInput, Button } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';
import Dropdown from 'af-components/Controls/Dropdown';
import LabelWithColor from 'af-components/LabelWithColor';
import LoadingIndicator from 'af-components/LoadingIndicator';
import MaterialRateSource from 'af-components/MaterialRateSource';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import type { TableRef } from 'af-components/RHFTableCellComponents/SimpleTable';
import type { ColumnDef, FooterCellDef, FooterRowDef } from 'af-components/RHFTableCellComponents/SimpleTable/types';
import LinkCell from 'af-components/Table/Cells/LinkCell';
import TextCell from 'af-components/Table/Cells/TextCell';
import DateFilter from 'af-components/Table/Filters/DateFilter';
import BooleanCell from 'af-components/Table6/Cells/BooleanCell';
import Tooltip from 'af-components/Tooltip';

import CLIENT from 'af-routes/client';

import * as BillingCodeActions from 'af-actions/billingCode/index';
import * as JobActions from 'af-actions/jobs';
import * as JobTrackingActions from 'af-actions/jobTracking';

import styles from './styles.module.scss';
import type { JobPreviewOutletContext } from '../types';
import JobInSyncQueue from './JobInSyncQueue';
import JobTrackingNoTemplate from './JobTrackingNoTemplate';

enum JobTrackingColumnDisplay {
	HIDE_ALL = 'HIDE_ALL',
	SHOW_USED = 'SHOW_USED',
	SHOW_ALL = 'SHOW_ALL',
}

interface JobTrackingColumnDisplayOption {
	id: JobTrackingColumnDisplay;
	label: string;
}

const JOB_TRACKING_COLUMN_DISPLAY_OPTIONS: JobTrackingColumnDisplayOption[] = [
	{
		id: JobTrackingColumnDisplay.HIDE_ALL,
		label: 'Hide All',
	},
	{
		id: JobTrackingColumnDisplay.SHOW_USED,
		label: 'Show Only Used',
	},
	{
		id: JobTrackingColumnDisplay.SHOW_ALL,
		label: 'Show All',
	},
];

const MODAL_BODY = <div>
	<div className={styles['info-margin']}>Recalculation will start immediately and may take a few minutes depending on the size of the job.</div>
	<div>Refresh the page after a few minutes to see updated values.</div>
</div>;

interface SettingProps {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
}

type Props = ConnectedProps<typeof connector> & SettingProps;

const DATE_OPTIONS = [
	{ id: TimePeriodRecurrence.DAILY, label: 'Daily' },
	{ id: TimePeriodRecurrence.WEEKLY, label: 'Weekly' },
	{ id: TimePeriodRecurrence.MONTHLY, label: 'Monthly' },
	{ id: TimePeriodRecurrence.FISCAL_YEAR, label: 'Fiscal Year' },
	{ id: TimePeriodRecurrence.YEAR_TO_DATE, label: 'Year to Date' },
	{ id: TimePeriodRecurrence.ALL, label: 'All' },
];

const setLocalStorageStartDate = (date: Date = new Date()) => {
	SettingsUtils.setItemWithFormatter(
		SettingsKeys.JOB_TRACKING_START_DATE(),
		date,
		(value: Date) => TimeUtils.formatDate(value, TimeFormat.FULL_DATE),
		BrowserStorageEnum.LOCAL_STORAGE
	);
};

const setLocalStorageEndDate = (date: Date = new Date()) => {
	SettingsUtils.setItemWithFormatter(
		SettingsKeys.JOB_TRACKING_END_DATE(),
		date,
		(value: Date) => TimeUtils.formatDate(value, TimeFormat.FULL_DATE),
		BrowserStorageEnum.LOCAL_STORAGE
	);
};

const setLocalStoragePeriod = (period: TimePeriodRecurrence) => {
	SettingsUtils.setItem(SettingsKeys.JOB_TRACKING_PERIOD(), period, BrowserStorageEnum.LOCAL_STORAGE);
};

const normalizeDateToDate = (item: string) => {
	return TimeUtils.normalizeDateToDate(item, TimeFormat.FULL_DATE);
};

const getSecondaryGroupsLabel = (groups: string[] = []): string => {
	const otherGroups = groups.slice(1).filter(Boolean);
	return otherGroups.join(', ');
};

const makeMaterialColumnKey = (
	materialId: number,
	materialCostSource: MaterialCostSource
): string => {
	return JSON.stringify([materialId, materialCostSource]);
};

const materialSourceLabelMap: Record<MaterialCostSource, string> = {
	[MaterialCostSource.COMPANY]: 'Company Rates',
	[MaterialCostSource.COMPANY_MATERIAL]: 'Material Rates',
	[MaterialCostSource.JOB]: 'Job Rates',
	[MaterialCostSource.JOB_MATERIAL]: 'Job Material Rates',
};

const materialSourceOrder: Record<MaterialCostSource, number> = {
	[MaterialCostSource.COMPANY]: 0,
	[MaterialCostSource.COMPANY_MATERIAL]: 1,
	[MaterialCostSource.JOB]: 2,
	[MaterialCostSource.JOB_MATERIAL]: 3,
};

const TOTALS_TOOLTIP_TITLE = 'Total Cost';
const MATERIAL_TOTALS_TOOLTIP_MESSAGE = 'Material Cost ($) x Quantity (x), summed across all Material rows';
const BILLING_CODE_TOTALS_TOOLTIP_MESSAGE = 'Total is calculated by summing all products of unit price * quantity';
const PAGE_SIZE = 1000;

const getMaterialSourceOrder = (source: MaterialCostSource): number => {
	return materialSourceOrder[source];
};

type JobTrackingRowFM = W_Job_JobTrackingEntries_VM_Row & SimpleTableRow;

type JTColumnDef = ColumnDef<JobTrackingRowFM>;

type UnitPriceHeader = Pick<BillingCodeVM, 'unitPrice' | 'unit'>;

const toRowFM = (rows: W_Job_JobTrackingEntries_VM_Row[]): JobTrackingRowFM[] => {
	return rows.map((r, idx) => ({
		...r,
		index: idx,
	}));
};

interface FormValues {
	rows: JobTrackingRowFM[];
}

const JobTracking: React.FC<Props> = (props) => {
	const {
		findBillingCodesForJobId,
		findJobTrackingTemplateColumnsForJobId,
		getJobTrackingTable,
		getJobTrackingTotals,
		getJobTrackingTemplate,
		getJobSyncQueue,
		companyData,
		exportJobTrackingXlsx,
		triggerResync,
	} = props;

	const navigate = useNavigate();

	const {
		jobId,
		companyName,
		orgAlias,
		hasPermissionsToAccessJobTracking,
	} = useOutletContext<JobPreviewOutletContext>();

	const tableRef = React.useRef<TableRef>(null);
	const [jobHasTemplate, setJobHasTemplate] = React.useState(false);
	const [jobSyncQueueStatus, setJobSyncQueueStatus] = React.useState<W_Job_JobTrackingSyncStatus_VM>({ status: null, reason: null });
	const [billingCodes, setBillingCodes] = React.useState<BillingCodeVM[]>([]);
	const [templateColumns, setTemplateColumns] = React.useState<W_Job_JobTrackingTemplateColumn_VM[]>([]);
	const [startDate, setStartDate] = React.useState(TimeUtils.positionDate(props.startDate, 'start', 'day'));
	const [endDate, setEndDate] = React.useState(TimeUtils.positionDate(props.endDate, 'end', 'day'));
	const [period, setPeriod] = React.useState(props.period);
	const [isLoading, setIsLoading] = React.useState<boolean>(true);
	const [isLoadingMore, setIsLoadingMore] = React.useState<boolean>(false);
	const [billingCodesDisplay, setBillingCodesDisplay] = React.useState<JobTrackingColumnDisplay>(
		JobTrackingColumnDisplay.HIDE_ALL
	);
	const [materialsDisplay, setMaterialsDisplay] = React.useState<JobTrackingColumnDisplay>(
		JobTrackingColumnDisplay.HIDE_ALL
	);
	const [totals, setTotals] = React.useState<W_Job_JobTrackingTotals_VM_Totals>();
	const [searchText, setSearchText] = React.useState('');
	const [debouncedSearchText, setDebouncedSearchText] = React.useState('');
	const [highlightedRowIndex, setHighlightedRowIndex] = React.useState<Nullable<number>>(null);
	const [isExportingXlsx, setIsExportingXlsx] = React.useState(false);
	const [resyncWarningModalOpen, setResyncWarningModalOpen] = React.useState(false);
	const [hasMoreData, setHasMoreData] = React.useState(true);
	const [cursor, setCursor] = React.useState<string | null>(null);

	const scrollElementRef = React.useRef<Nullable<HTMLElement>>(null);
	const loadingRef = React.useRef<boolean>(false);
	const requestIdRef = React.useRef(0);

	const { control } = useForm<FormValues>({
		values: { rows: [] },
	});

	const { fields, replace, append } = useFieldArray<FormValues, 'rows'>({
		control,
		name: 'rows',
	});

	const shouldFilterByDate = React.useMemo(() => {
		return period !== TimePeriodRecurrence.ALL;
	}, [period]);

	const shouldFilterStartDate = React.useMemo(() => {
		return shouldFilterByDate ? TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY) : '';
	}, [shouldFilterByDate, startDate]);

	const shouldFilterEndDate = React.useMemo(() => {
		return shouldFilterByDate ? TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY) : '';
	}, [shouldFilterByDate, endDate]);

	const onRowClicked = React.useCallback((row: Row<JobTrackingRowFM>) => {
		setHighlightedRowIndex(row.original.index);
	}, []);

	const getCellClassName = React.useCallback((cell: Cell<JobTrackingRowFM, unknown>) => {
		if (cell.row.original.index === highlightedRowIndex) {
			return styles['highlighted-cell'];
		}

		return '';
	}, [highlightedRowIndex]);

	const fetchTemplate = React.useCallback(async () => {
		const jobTrackingTemplate = await getJobTrackingTemplate(jobId);
		setJobHasTemplate(!!jobTrackingTemplate?.id);
	}, [getJobTrackingTemplate, jobId]);

	const checkSyncQueue = React.useCallback(async () => {
		const jobSyncQueue = await getJobSyncQueue(jobId);
		setJobSyncQueueStatus(jobSyncQueue);
	}, [getJobSyncQueue, jobId]);

	const setTemplateToJob = React.useCallback(() => {
		fetchTemplate();
		checkSyncQueue();
	}, [fetchTemplate, checkSyncQueue]);

	const shouldShowSyncMessage = React.useMemo(() => {
		if (jobSyncQueueStatus?.status === JobTrackingSyncQueueStatus.PENDING || jobSyncQueueStatus?.status === JobTrackingSyncQueueStatus.IN_PROGRESS) {
			return true;
		}

		return false;
	}, [jobSyncQueueStatus]);

	const openResyncModal = React.useCallback(() => {
		setResyncWarningModalOpen(true);
	}, []);

	const closeResyncModal = React.useCallback(() => {
		setResyncWarningModalOpen(false);
	}, []);

	const handleResyncConfirm = React.useCallback(async () => {
		closeResyncModal();
		await triggerResync(jobId);
	}, [closeResyncModal, jobId, triggerResync]);

	const fetchBillingCodesAndColumns = React.useCallback(async () => {
		if (!!jobHasTemplate) {
			const [fetchedBillingCodes, fetchedTemplateColumns] = await Promise.all([
				findBillingCodesForJobId(jobId),
				findJobTrackingTemplateColumnsForJobId(jobId),
			]);

			setBillingCodes(fetchedBillingCodes);
			setTemplateColumns((fetchedTemplateColumns.columns ?? []).slice().sort((a, b) => (a.index ?? 0) - (b.index ?? 0)));
		}
	}, [findBillingCodesForJobId, findJobTrackingTemplateColumnsForJobId, jobHasTemplate, jobId]);

	const loadMore = React.useCallback(async (currentCursor: string | null) => {
		const isFirstPage = !currentCursor;

		if (!isFirstPage && loadingRef.current) {
			return;
		}

		const requestId = ++requestIdRef.current;

		if (!jobHasTemplate || !templateColumns.length) {
			loadingRef.current = false;
			setHasMoreData(false);
			return;
		}

		loadingRef.current = true;

		if (isFirstPage) {
			setIsLoading(true);
		} else {
			setIsLoadingMore(true);
		}

		try {
			const result = await getJobTrackingTable(
				jobId,
				new TableQuery({ page: 0, pageSize: PAGE_SIZE, sortBy: [], filterByText: debouncedSearchText }),
				shouldFilterStartDate,
				shouldFilterEndDate,
				currentCursor ?? ''
			);

			if (requestId !== requestIdRef.current) {
				return;
			}

			const rows = toRowFM((result?.rows ?? []) as W_Job_JobTrackingEntries_VM_Row[]);

			if (isFirstPage) {
				replace(rows);

				// start loading totals in the background
				setIsLoading(false);

				const tableTotals = await getJobTrackingTotals(
					jobId,
					new TableQuery({ page: 0, pageSize: PAGE_SIZE, sortBy: [], filterByText: debouncedSearchText }),
					shouldFilterStartDate,
					shouldFilterEndDate
				);
				setTotals(tableTotals.totals);
			} else {
				append(rows);
			}

			const nextCursor = result?.nextCursor ?? null;
			setCursor(nextCursor);
			setHasMoreData(!!nextCursor);
		} catch (err) {
			console.error(err);
		} finally {
			if (requestId === requestIdRef.current) {
				loadingRef.current = false;
				setIsLoading(false);
				setIsLoadingMore(false);
			}
		}
	}, [
		jobHasTemplate,
		templateColumns.length,
		getJobTrackingTable,
		getJobTrackingTotals,
		jobId,
		debouncedSearchText,
		shouldFilterStartDate,
		shouldFilterEndDate,
		replace,
		append,
	]);

	const loadFirstPage = React.useCallback(() => {
		setCursor(null);
		setHasMoreData(true);
		loadMore(null);
	}, [loadMore]);

	const checkAndLoad = React.useCallback((el: HTMLElement | null) => {
		if (!el || !hasMoreData || loadingRef.current) {
			return;
		}

		const { scrollHeight, scrollTop, clientHeight } = el;
		const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

		// load new page when 2 screens from the bottom
		if (distanceFromBottom < clientHeight * 2) {
			loadMore(cursor);
		}
	}, [hasMoreData, cursor, loadMore]);

	const handleScrollCapture = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
		const el = e.target as HTMLElement;
		if (el.scrollHeight <= el.clientHeight) {
			return;
		}
		scrollElementRef.current = el;
		checkAndLoad(el);
	}, [checkAndLoad]);

	const changePeriod = React.useCallback((_period: TimePeriodRecurrence, _selected: Date) => {
		let _startDate: Date = _selected, _endDate: Date = _selected;

		switch (_period) {
			case TimePeriodRecurrence.MONTHLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'month'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'month'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.WEEKLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'week'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'week'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.FISCAL_YEAR:
				const fiscalMonth = companyData.fiscalYearStartMonth!;
				const selMonth = _selected.getMonth() + 1;
				let fyYear = _selected.getFullYear();

				if (selMonth < fiscalMonth) {
					fyYear -= 1;
				}

				const fyStart = new Date(fyYear, fiscalMonth, 0);
				const fyEnd = new Date(fyYear + 1, fiscalMonth - 1, 0);
				_startDate = TimeUtils.positionDate(fyStart, 'start', 'month');
				_endDate = TimeUtils.positionDate(fyEnd, 'end', 'day');
				break;
			case TimePeriodRecurrence.YEAR_TO_DATE:
				_startDate = TimeUtils.positionDate(new Date(), 'start', 'year');
				_endDate = new Date();
				break;
			case TimePeriodRecurrence.DAILY:
				_startDate = TimeUtils.positionDate(_selected, 'start', 'day');
				_endDate = TimeUtils.positionDate(_selected, 'end', 'day');
				break;
			default:
				break;
		}

		setStartDate(_startDate);
		setEndDate(_endDate);
		setPeriod(_period);
	}, [companyData.fiscalYearStartMonth]);

	const filterByDate = React.useCallback((_startDate: Date, _endDate: Date) => {
		setStartDate(TimeUtils.positionDate(_startDate, 'start', 'day'));
		setEndDate(TimeUtils.positionDate(_endDate, 'end', 'day'));
	}, []);

	const materialHeaders = React.useMemo<
		W_Job_JobTrackingEntries_VM_MaterialHeader[]
	>(() => {
		const headerMap = (fields as JobTrackingRowFM[]).reduce<
			Record<string, W_Job_JobTrackingEntries_VM_MaterialHeader>
		>((acc, row) => {
			const materials = Object.values(row.materials ?? {});

			for (const material of materials) {
				if (!acc[material.key]) {
					acc[material.key] = {
						materialId: material.materialId,
						displayName: material.displayName,
						description: material.description,
						unitPrice: `${material.unitPrice ?? ''}`,
						unit: material.unit,
						group: material.group,
						groups: material.groups ?? [],
						materialCostSource: material.materialCostSource,
						key: material.key,
						hasMaterialCost: material.hasMaterialCost,
					};
				}
			}

			return acc;
		}, {});

		return Object.values(headerMap).sort((a, b) => {
			if (!!a.group !== !!b.group) {
				return a.group ? -1 : 1;
			}

			if (a.group && b.group) {
				const groupCompare = a.group.localeCompare(b.group);

				if (groupCompare !== 0) {
					return groupCompare;
				}
			}

			const materialCompare = (a.displayName ?? '').localeCompare(
				b.displayName ?? ''
			);

			if (materialCompare !== 0) {
				return materialCompare;
			}

			return getMaterialSourceOrder(a.materialCostSource)
				- getMaterialSourceOrder(b.materialCostSource);
		});
	}, [fields]);

	const usedMaterialKeys = React.useMemo(() => {
		const result: Record<string, true> = {};

		for (const row of fields as JobTrackingRowFM[]) {
			for (const material of Object.values(row.materials ?? {})) {
				if (material.quantity !== 0) {
					const materialKey = makeMaterialColumnKey(
						material.materialId,
						material.materialCostSource
					);

					result[materialKey] = true;
				}
			}
		}

		return result;
	}, [fields]);

	const visibleMaterialHeaders = React.useMemo(() => {
		switch (materialsDisplay) {
			case JobTrackingColumnDisplay.HIDE_ALL:
				return [];

			case JobTrackingColumnDisplay.SHOW_USED:
				return materialHeaders.filter((material) => {
					const materialKey = makeMaterialColumnKey(
						material.materialId,
						material.materialCostSource
					);

					return !!usedMaterialKeys[materialKey];
				});

			case JobTrackingColumnDisplay.SHOW_ALL:
				return materialHeaders;

			default:
				return [];
		}
	}, [materialHeaders, materialsDisplay, usedMaterialKeys]);

	const usedBillingCodeIds = React.useMemo(
		() =>
			(fields as JobTrackingRowFM[]).reduce<Record<number, true>>((result, row) => {
				return Object.values(row.billingCodes ?? {}).reduce((_result, billingCode) => {
					if (billingCode.quantity !== 0) {
						_result[billingCode.id] = true;
					}

					return _result;
				}, result);
			}, {}),
		[fields]
	);

	const visibleBillingCodes = React.useMemo(() => {
		switch (billingCodesDisplay) {
			case JobTrackingColumnDisplay.HIDE_ALL:
				return [];

			case JobTrackingColumnDisplay.SHOW_USED:
				return billingCodes.filter((billingCode) => !!usedBillingCodeIds[billingCode.id]);

			case JobTrackingColumnDisplay.SHOW_ALL:
				return billingCodes;

			default:
				return [];
		}
	}, [billingCodes, billingCodesDisplay, usedBillingCodeIds]);

	const renderTotalMaterialHeader = React.useCallback(() => {
		return (
			<div>
				TOTAL MATERIAL COST ($)
			</div>
		);
	}, []);

	const renderMaterialDisplayNameHeader = React.useCallback((material: W_Job_JobTrackingEntries_VM_MaterialHeader) => () => {
		return (
			<span>
				{material.displayName}
			</span>
		);
	}, []);

	const renderMaterialDetailsHeader = React.useCallback((material: W_Job_JobTrackingEntries_VM_MaterialHeader) => () => {
		const secondaryGroupsLabel = getSecondaryGroupsLabel(material.groups);

		return (
			<div className={styles['material-details-header']}>
				<div>{secondaryGroupsLabel}</div>
				<div>{material.description}</div>
			</div>
		);
	}, []);

	const renderMaterialUnitHeader = React.useCallback(() => {
		return <span>Unit</span>;
	}, []);

	const renderMaterialUnitCell = React.useCallback(() => (cell: CellContext<JobTrackingRowFM, string>) => {
		const value = cell.getValue();

		if (!value) {
			return <span className={`${styles['job-tracking__empty-cell--left-aligned']} ${styles['white-background-cell']}`}>-</span>;
		}

		return <TextCell className={`${styles['info-cell--left-aligned']} ${styles['white-background-cell']}`} value={value} />;
	}, []);

	const renderMaterialSourceHeader = React.useCallback((source: MaterialCostSource) => () => {
		const _source = materialSourceLabelMap[source] ?? source;
		return (
			<span className={styles['material-source']}><MaterialRateSource rateSource={source} />{_source}</span>
		);
	}, []);

	const renderMaterialCostHeader = React.useCallback(() => {
		return <span>Material Cost</span>;
	}, []);

	const renderMaterialQtyHeader = React.useCallback(() => {
		return <span>Qty</span>;
	}, []);

	const renderMaterialCostCell = React.useCallback(() => (cell: CellContext<JobTrackingRowFM, string>) => {
		const value = cell.getValue();

		const className: string[] = [styles['info-cell'], styles['dollar-cell'], styles['grey-background-cell'], styles['grey-text']];

		if (value === null || value === undefined || value === '') {
			return <span className={`${styles['job-tracking__empty-cell--right-aligned']} ${styles['grey-background-cell']}`}>-</span>;
		}

		return <div className={className.join(' ')}><div>$</div><div>{dollarUnitFormatter().format(+value)}</div></div>;
	}, []);

	const renderMaterialQtyCell = React.useCallback(() => (cell: CellContext<JobTrackingRowFM, number>) => {
		const value = cell.getValue();

		if (value === null || value === undefined) {
			return <span className={`${styles['job-tracking__empty-cell--right-aligned']} ${styles['white-background-cell']}`}>-</span>;
		}

		return <TextCell className={`${styles['info-cell--right-aligned']} ${styles['white-background-cell']}`} value={`${value}`} />;
	}, []);

	const columnsMaterials = React.useMemo<JTColumnDef[]>(() => {
		if (!visibleMaterialHeaders.length) {
			return [];
		}

		const groupedByPrimaryGroup = visibleMaterialHeaders.reduce<
			Record<string, W_Job_JobTrackingEntries_VM_MaterialHeader[]>
		>((acc, material) => {
			const groupKey = material.group ?? 'Ungrouped';

			if (!acc[groupKey]) {
				acc[groupKey] = [];
			}

			acc[groupKey].push(material);
			return acc;
		}, {});

		const materialGroupColumns: JTColumnDef[] = Object.keys(groupedByPrimaryGroup)
			.sort((a, b) => a.localeCompare(b))
			.map((groupName) => {
				const groupMaterials = groupedByPrimaryGroup[groupName];

				const groupedByMaterialId = groupMaterials.reduce<Record<string, W_Job_JobTrackingEntries_VM_MaterialHeader[]>>((acc, material) => {
					const materialKey = `${material.materialId}`;

					if (!acc[materialKey]) {
						acc[materialKey] = [];
					}

					acc[materialKey].push(material);
					return acc;
				}, {});

				return {
					id: `materials-group:${groupName}`,
					header: groupName,
					columns: Object.keys(groupedByMaterialId)
						.sort((a, b) => {
							const aMaterial = groupedByMaterialId[a][0];
							const bMaterial = groupedByMaterialId[b][0];

							const nameCompare = (aMaterial.displayName ?? '').localeCompare(bMaterial.displayName ?? '');
							if (nameCompare !== 0) {
								return nameCompare;
							}

							return (aMaterial.description ?? '').localeCompare(bMaterial.description ?? '');
						})
						.map((materialIdKey) => {
							const materialVariants = groupedByMaterialId[materialIdKey]
								.slice()
								.sort((a, b) => getMaterialSourceOrder(a.materialCostSource) - getMaterialSourceOrder(b.materialCostSource));

							const baseMaterial = materialVariants[0];

							return {
								id: `material:${baseMaterial.materialId}`,
								header: renderMaterialDisplayNameHeader(baseMaterial),
								columns: [
									{
										id: `material-details:${baseMaterial.materialId}`,
										header: renderMaterialDetailsHeader(baseMaterial),
										columns: materialVariants.map((materialVariant) => ({
											id: `material-source:${materialVariant.key}`,
											header: renderMaterialSourceHeader(materialVariant.materialCostSource),
											columns: [
												{
													id: `material-cost:${materialVariant.key}`,
													header: renderMaterialCostHeader,
													accessor: (row: JobTrackingRowFM) => row.materials?.[materialVariant.key]?.unitPrice ?? null,
													cell: renderMaterialCostCell(),
													enableSorting: false,
													size: 110,
												},
												{
													id: `material-qty:${materialVariant.key}`,
													header: renderMaterialQtyHeader,
													accessor: (row: JobTrackingRowFM) => row.materials?.[materialVariant.key]?.quantity ?? null,
													cell: renderMaterialQtyCell(),
													enableSorting: false,
													size: 80,
												},
												{
													id: `material-unit:${materialVariant.key}`,
													header: renderMaterialUnitHeader,
													accessor: (row: JobTrackingRowFM) => row.materials?.[materialVariant.key]?.unit ?? null,
													cell: renderMaterialUnitCell(),
													enableSorting: false,
													size: 80,
												},
											],
										})),
									},
								],
							};
						}),
				};
			});

		return [
			{
				id: 'materials-root',
				header: () => <div>
					<BillingTypeIndicator
						billingType={WorkSummaryBillingType.MATERIAL_COST}
						className={bemElement('field-report__work-summary__billable-work__legend__single', 'billing-type-indicator')}
						small={true}
					/>
					Materials
				</div>,
				columns: materialGroupColumns,
				meta: {
					columnAccessor: 'materials-root',
					color: '#575D63',
				},
			},
		];
	}, [
		visibleMaterialHeaders,
		renderMaterialDisplayNameHeader,
		renderMaterialDetailsHeader,
		renderMaterialSourceHeader,
		renderMaterialCostHeader,
		renderMaterialQtyHeader,
		renderMaterialUnitHeader,
		renderMaterialCostCell,
		renderMaterialQtyCell,
		renderMaterialUnitCell,
	]);

	const renderTotalBillableHeader = React.useCallback(() => {
		return (
			<div>
				TOTAL BILLABLE WORK ($)
			</div>
		);
	}, []);

	const debouncedSetSearch = React.useMemo(
		() => debounce((value: string) => {
			setDebouncedSearchText(value);
		}, SEARCH_DELAY),
		[]
	);

	const handleSearchChange = React.useCallback((searchValue: string) => {
		setSearchText(searchValue);
		debouncedSetSearch(searchValue);
	}, [debouncedSetSearch]);

	const exportAsXlsx = React.useCallback(async () => {
		setIsExportingXlsx(true);

		try {
			await exportJobTrackingXlsx(jobId, {
				filterByText: debouncedSearchText,
				startDate: shouldFilterStartDate,
				endDate: shouldFilterEndDate,
				materialColumnKeys: visibleMaterialHeaders.map((material) => material.key),
				billingCodeIds: visibleBillingCodes.map((billingCode) => billingCode.id),
			});
		} finally {
			setIsExportingXlsx(false);
		}
	}, [
		debouncedSearchText,
		exportJobTrackingXlsx,
		jobId,
		shouldFilterStartDate,
		shouldFilterEndDate,
		visibleBillingCodes,
		visibleMaterialHeaders,
	]);

	const renderColumnDisplayOption = React.useCallback((
		option: Nullable<JobTrackingColumnDisplayOption>
	) => {
		return (
			<div>
				{option?.label ?? ''}
			</div>
		);
	}, []);

	const handleColumnDisplayChange = React.useCallback(
		(onChange: (display: JobTrackingColumnDisplay) => void) => (option: Nullable<JobTrackingColumnDisplayOption>) => {
			if (!option) {
				return;
			}

			onChange(option.id);
		}, []);

	const renderDisplayOption = React.useCallback((label: string) => (option: Nullable<JobTrackingColumnDisplayOption>) => (
		<div>
			{label}: {option?.label ?? ''}
		</div>
	), []);

	const renderColumnDisplayDropdown = React.useCallback((
		label: string,
		value: JobTrackingColumnDisplay,
		onChange: (display: JobTrackingColumnDisplay) => void
	) => {
		const selectedOption = JOB_TRACKING_COLUMN_DISPLAY_OPTIONS.find((option) => {
			return option.id === value;
		}) ?? JOB_TRACKING_COLUMN_DISPLAY_OPTIONS[0];

		return (
			<Dropdown<JobTrackingColumnDisplayOption>
				defaultValue={selectedOption}
				filterable={false}
				isWhite={true}
				onValueChange={handleColumnDisplayChange(onChange)}
				options={JOB_TRACKING_COLUMN_DISPLAY_OPTIONS}
				renderMenuItem={renderColumnDisplayOption}
				renderSelected={renderDisplayOption(label)}
				valueKey="id"
				withBorder={true}
				withCaret={true}
			/>
		);
	}, [handleColumnDisplayChange, renderColumnDisplayOption, renderDisplayOption]);

	const renderAdditionalFilter = React.useCallback(() => {
		return (
			<div className={styles['table-filter']}>
				<div className={styles['table-filter__left']}>
					<div className={styles['search-bar']}>
						<SearchInput
							name="searchQuery"
							onChange={handleSearchChange}
							placeholder="Search"
							value={searchText}
						/>
					</div>

					<DateFilter
						changePeriod={changePeriod}
						endDate={endDate}
						onChange={filterByDate}
						options={DATE_OPTIONS}
						period={period}
						startDate={startDate}
					/>

					<div className={styles['table-filter__column-filters']}>
						{renderColumnDisplayDropdown(
							'Materials',
							materialsDisplay,
							setMaterialsDisplay
						)}

						{renderColumnDisplayDropdown(
							'Billing Codes',
							billingCodesDisplay,
							setBillingCodesDisplay
						)}
					</div>
				</div>

				<div className={styles['table-filter__right']}>
					{companyData.isCompanyAdmin &&
						<Button
							icon="ArrowRotateRefresh"
							label="Recalculate Job Tracking"
							onClick={openResyncModal}
							style="link-danger"
						/>
					}
					<Button
						disabled={isExportingXlsx}
						icon="ActionDownload"
						label="Export as XLSX"
						onClick={exportAsXlsx}
						style="link"
					/>
				</div>
				<ConfirmationModal
					body={MODAL_BODY}
					closeModal={closeResyncModal}
					confirmAction={handleResyncConfirm}
					confirmText={'Confirm'}
					modalStyle="warning"
					showModal={resyncWarningModalOpen}
					size="md"
					title={'Recalculate Job Tracking'}
				/>
			</div>
		);
	}, [
		billingCodesDisplay,
		changePeriod,
		endDate,
		exportAsXlsx,
		filterByDate,
		handleSearchChange,
		materialsDisplay,
		period,
		searchText,
		startDate,
		renderColumnDisplayDropdown,
		isExportingXlsx,
		openResyncModal,
		handleResyncConfirm,
		closeResyncModal,
		resyncWarningModalOpen,
		companyData.isCompanyAdmin,
	]);

	const renderTextCell = React.useCallback((rightAlign?: boolean) => (cell: CellContext<JobTrackingRowFM, string>) => {
		const style = rightAlign ? styles['info-cell--right-aligned'] : styles['info-cell--left-aligned'];
		return <TextCell className={style} emptyMessage="-" value={cell.getValue()} />;
	}, []);

	const renderDateCell = React.useCallback(() => (cell: CellContext<JobTrackingRowFM, string>) => {
		const dbDate = cell.getValue();
		const date = TimeUtils.formatDate(dbDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);

		return <TextCell className={styles['info-cell']} value={date} />;
	}, []);

	const renderUnitPriceHeader = React.useCallback((billingCode: UnitPriceHeader) => {
		const _value = billingCode.unitPrice !== null && billingCode.unitPrice !== undefined
			? +billingCode.unitPrice
			: null;

		if (_value === null || Number.isNaN(_value)) {
			return null;
		}

		const _billingUnit: QuantityUnitType = QuantityUnitMap[billingCode.unit?.replace('/', '_')];
		const _unit = _billingUnit;

		const convertedUnitPrice = _unit !== _billingUnit ? convertUnits(_value, _unit, _billingUnit) : null;

		const _renderValue = convertedUnitPrice
			? `$${dollarUnitFormatter(8, 0).format(convertedUnitPrice)} per ${_unit ? _unit.replace('_', '/') : '-'} ($${dollarUnitFormatter(4, 0).format(_value)} per ${_billingUnit?.replace('_', '/')})`
			: `$${dollarUnitFormatter(4, 0).format(_value)}`;

		return _renderValue;
	}, []);

	const renderLinkCell = React.useCallback(() => (_cell: CellContext<JobTrackingRowFM, string>) => {
		if (!_cell.cell.row.original.workOrders.length) {
			return (
				<span className={styles['job-tracking_table__empty-cell']}>
					-
				</span>
			);
		}

		const links = _cell.cell.row.original.workOrders.map((workOrder) => {
			const path = workOrder.workOrderId
				? getFullClientUrl(orgAlias, CLIENT.COMPANY.FIELD_REPORT.ALL_REPORTS(`${workOrder.workOrderId}`, orgAlias, companyName))
				: '';

			return <LinkCell key={workOrder.workOrderId} label={`${workOrder.workOrderCode!}`} path={path} />;
		});

		return (
			<div >
				{links}
			</div>
		);
	}, [companyName, orgAlias]);

	const renderBillingCodeMainHeader = React.useCallback((bc: BillingCodeVM) => () => {
		const unit = bc.unit
			? bc.unit.replace('_', '/')
			: '';

		return (
			<span>
				<span className={styles['billing-code-name']}>
					<b>{bc.customerId}</b>

					{bc.deletedAt && (
						<span className={`icon-archive ${styles['billing-code-archive-icon']}`} />
					)}
				</span>

				<br />

				<span>
					{renderUnitPriceHeader(bc)}
					{unit ? ` ${unit}` : ''}
				</span>
			</span>
		);
	}, [renderUnitPriceHeader]);
	const renderBillingCodeDescriptionHeader = React.useCallback((bc: BillingCodeVM) => () => {
		return (
			<span>
				<span>{bc.description || '\u00A0'}</span>
			</span>
		);
	}, []);

	const renderBillingCodeQuantityHeader = React.useCallback(() => {
		return <span>Qty</span>;
	}, []);

	const renderBillingCodeQuantityCell = React.useCallback(() => (cell: CellContext<JobTrackingRowFM, number>) => {
		const value = cell.getValue();

		if (value === null || value === undefined) {
			return <span className={`${styles['job-tracking__empty-cell--right-aligned']} ${styles['white-background-cell']}`}>-</span>;
		}

		return <TextCell className={`${styles['info-cell--right-aligned']} ${styles['white-background-cell']}`} value={`${value}`} />;
	}, []);

	const renderDollarCell = React.useCallback(() => (_cell: CellContext<JobTrackingRowFM, number>) => {
		const className: string[] = [styles['info-cell'], styles['dollar-cell']];
		if (_cell.getValue() === null) {
			className.push(styles['job-tracking__empty-cell']);
			className.push(styles['job-tracking__empty-cell--right-aligned']);
			return (
				<span className={className.join(' ')}>
					-
				</span>
			);
		}

		return <div className={className.join(' ')}><div className={styles['grey-text']}>$</div><div>{dollarUnitFormatter().format(_cell.getValue())}</div></div>;
	}, []);

	const renderTotalsTooltip = React.useCallback((isMaterial: boolean) => {
		return (
			<div className={styles['totals-tooltip']}>
				{isMaterial && (
					<div className={styles['totals-tooltip__title']}>
						{TOTALS_TOOLTIP_TITLE}
					</div>
				)}

				<div
					className={[
						styles['totals-tooltip__message'],
						!isMaterial ? styles['totals-tooltip__message--billing-code'] : '',
					].filter(Boolean).join(' ')}
				>
					{isMaterial
						? MATERIAL_TOTALS_TOOLTIP_MESSAGE
						: BILLING_CODE_TOTALS_TOOLTIP_MESSAGE}
				</div>
			</div>
		);
	}, []);

	const renderDollarValue = React.useCallback((
		value: Nullable<number>,
		tooltip?: React.JSX.Element,
		greyBackground?: boolean,
		greyDollar?: boolean,
		greyZero?: boolean,
		tooltipClassName?: string
	) => {
		const className: string[] = [styles['info-cell'], styles['dollar-cell']];
		if (greyBackground) {
			className.push(styles['grey-background-cell']);
		}
		if (value === null) {
			className.push(styles['job-tracking__empty-cell']);
			className.push(styles['job-tracking__empty-cell--right-aligned']);
			return (
				<span className={className.join(' ')}>
					-
				</span>
			);
		}

		const dollarClassname = greyDollar === false ? '' : styles['grey-text'];

		const zeroClassname = greyZero === true && value === 0 ? styles['grey-text'] : '';

		return <div className={className.join(' ')}>
			<div className={dollarClassname}>$</div>
			<div className={styles['totals-value']}>
				{tooltip && (
					<Tooltip
						className={[
							styles['totals-tooltip-popover'],
							tooltipClassName,
						].filter(Boolean).join(' ')}
						message={tooltip}
					>
						<span className={`icon-info ${styles['info-icon']}`} />
					</Tooltip>
				)}
				<div className={zeroClassname}>{dollarUnitFormatter().format(value)}</div>
			</div>
		</div>;
	}, []);

	const renderTimeTotalsValue = React.useCallback((value: number) => {
		const className: string[] = [styles['totals-value-cell'], styles['grey-background-cell']];
		return <TextCell className={className.join(' ')} emptyMessage="-" value={value} />;
	}, []);

	const renderInformationValue = React.useCallback(
		() => (_cell: CellContext<JobTrackingRowFM, unknown>) => {
			const rawValue = _cell.getValue<unknown>();

			const renderSingleValue = (value: unknown) => {
				if (value === null || value === undefined) {
					return null;
				}

				const alignment = Number.isNaN(+value) ? 'left' : 'right';

				const stringValue = `${value}`;

				switch (stringValue) {
					case 'false':
						return <BooleanCell className={styles[`info-cell--${alignment}-aligned`]} value={false} />;
					case 'true':
						return <BooleanCell className={styles[`info-cell--${alignment}-aligned`]} value={true} />;
					case 'null':
					case '[null]':
						return null;
					default:
						return <TextCell className={styles[`info-cell--${alignment}-aligned`]} emptyMessage="-" value={stringValue} />;
				}
			};

			// --- array case ---
			if (Array.isArray(rawValue)) {
				const rendered = rawValue
					.map(renderSingleValue)
					.filter((x): x is React.JSX.Element => !!x);

				if (!rendered.length) {
					return '';
				}

				// render with commas between

				return (
					<>
						{rendered.map((node, idx) => (
							<React.Fragment key={idx}>
								{idx > 0 ? ', ' : null}
								{node}
							</React.Fragment>
						))}
					</>
				);
			}

			// --- non-array case ---
			const single = renderSingleValue(rawValue);
			return <div className={styles['info-cell--left-aligned']}>{single ?? ''}</div>;
		},
		[]
	);

	const renderCrewTypeOptions = React.useCallback(() => (cell: CellContext<JobTrackingRowFM, W_Job_JobTrackingEntries_VM_CrewType[]>) => (
		<div className={`${styles['crew-type']} ${styles['info-cell']}`}>
			{(cell.getValue() ?? []).map((option) => (
				<LabelWithColor color={option.color} key={option.id} text={option.name} />
			))}
		</div>
	), []);

	const templateColumnsForTable = React.useMemo<JTColumnDef[]>(() => {
		return templateColumns.map((col) => {
			let cell = renderInformationValue();
			let accessor: (row: JobTrackingRowFM) => string | Date | W_Job_JobTrackingEntries_VM_WorkOrder[] | W_Job_JobTrackingEntries_VM_CrewType[] = (_row: W_Job_JobTrackingEntries_VM_Row) => _row.infoValues?.[col.name] ?? '';

			switch (col.type) {
				case JobTrackingTemplateColumnType.DATE:
					cell = renderDateCell();
					accessor = (_row: W_Job_JobTrackingEntries_VM_Row) => `${_row.date}`;
					break;

				case JobTrackingTemplateColumnType.WORK_ORDER_ID:
					cell = renderLinkCell();
					accessor = (_row: W_Job_JobTrackingEntries_VM_Row) => _row.workOrders;
					break;

				case JobTrackingTemplateColumnType.CREW_TYPE:
					cell = renderCrewTypeOptions();
					accessor = (_row: W_Job_JobTrackingEntries_VM_Row) => _row.crewTypes;
					break;

				default:
					break;
			}

			return {
				id: col.name,
				accessor,
				header: col.name,
				cell,
				autoHideEmptyColumn: false,
				enableSorting: false,
				isFixed: col.type === JobTrackingTemplateColumnType.DATE,
			};
		});
	}, [templateColumns, renderInformationValue, renderDateCell, renderLinkCell, renderCrewTypeOptions]);

	const columnsBillingCodes = React.useMemo<JTColumnDef[]>(() => {
		const list = visibleBillingCodes;

		if (!list.length) {
			return [];
		}

		const grouped = list.reduce<Record<string, BillingCodeVM[]>>((acc, bc) => {
			const key = bc.group?.name ?? 'Ungrouped';

			if (!acc[key]) {
				acc[key] = [];
			}

			acc[key].push(bc);

			return acc;
		}, {});

		const billingCodeGroupColumns: JTColumnDef[] = Object.keys(grouped)
			.sort((a, b) => {
				return (grouped[a][0].group?.index ?? 999)
					- (grouped[b][0].group?.index ?? 999);
			})
			.map((groupName) => {
				const groupBcs = grouped[groupName]
					.slice()
					.sort((a, b) => {
						return (a.customerId ?? '').localeCompare(b.customerId ?? '');
					});

				return {
					id: `group:${groupName}`,
					header: groupName,
					columns: groupBcs.map((bc) => ({
						id: `billing-code:${bc.id}`,
						header: renderBillingCodeMainHeader(bc),
						columns: [
							{
								id: `billing-code-details:${bc.id}`,
								header: renderBillingCodeDescriptionHeader(bc),
								columns: [
									{
										id: `billing-code-quantity:${bc.id}`,
										header: renderBillingCodeQuantityHeader,
										accessor: (row: JobTrackingRowFM) =>
											row.billingCodes?.[`${bc.id}`]?.quantity ?? null,
										cell: renderBillingCodeQuantityCell(),
										enableSorting: false,
										size: 120,
									},
								],
							},
						],
					})),
					meta: {
						columnAccessor: `group:${groupName}`,
						color: grouped[groupName][0].group?.color,
					},
				};
			});

		return [
			{
				id: 'billing-codes-root',
				header: () => (
					<div>
						<BillingTypeIndicator
							billingType={WorkSummaryBillingType.BILLABLE_WORK}
							className={bemElement(
								'field-report__work-summary__billable-work__legend__single',
								'billing-type-indicator'
							)}
							small={true}
						/>
						Billing Codes
					</div>
				),
				columns: billingCodeGroupColumns,
			},
		];
	}, [
		visibleBillingCodes,
		renderBillingCodeMainHeader,
		renderBillingCodeDescriptionHeader,
		renderBillingCodeQuantityHeader,
		renderBillingCodeQuantityCell,
	]);

	const timeTotalColumns = React.useMemo<JTColumnDef[]>(() => [
		{
			id: 'totalJobTime',
			accessor: 'totalJobTime',
			header: 'Job Hours',
			cell: renderTextCell(true),
			enableSorting: false,
		},
		{
			id: 'totalShopTime',
			accessor: 'totalShopTime',
			header: 'Shop Hours',
			cell: renderTextCell(true),
			enableSorting: false,
		},
		{
			id: 'totalTravelTime',
			accessor: 'totalTravelTime',
			header: 'Travel Hours',
			cell: renderTextCell(true),
			enableSorting: false,
		},
	], [renderTextCell]);

	const totalRevenueColumn = React.useMemo<JTColumnDef>(() => {
		return {
			id: 'totalRevenue',
			accessor: 'totalRevenue',
			header: 'TOTAL REVENUE ($)',
			cell: renderDollarCell(),
			enableSorting: false,
			size: 120,
		};
	}, [renderDollarCell]);

	const billableColumn = React.useMemo<JTColumnDef>(() => {
		return {
			id: 'totalBillableWork',
			accessor: 'totalBillableWork',
			header: renderTotalBillableHeader,
			cell: renderDollarCell(),
			enableSorting: false,
			size: 160,
		};
	}, [renderDollarCell, renderTotalBillableHeader]);

	const totalsRow: FooterRowDef = React.useMemo(() => {
		const cells: FooterCellDef[] = [];

		if (templateColumns.length) {
			cells.push({
				id: 'totals-message',
				placement: {
					kind: 'span',
					fromLeafId: templateColumns[0].name,
					span: templateColumns.length,
				},
				cell: (
					<div
						className={[
							styles['grey-background-cell'],
							styles['grey-text'],
							styles['totals-message'],
						].join(' ')}
					>
						Totals are calculated the following way:
					</div>
				),
			});
		}

		for (const materialHeader of visibleMaterialHeaders) {
			cells.push({
				id: `totals-material-cost:${materialHeader.key}`,
				placement: {
					kind: 'span',
					fromLeafId: `material-cost:${materialHeader.key}`,
					span: 3,
				},
				rightAligned: true,
				cell: renderDollarValue(
					totals?.materialTotals?.[materialHeader.key] ?? 0,
					renderTotalsTooltip(true),
					true,
					false,
					true
				),
			});
		}

		for (const billingCode of visibleBillingCodes) {
			cells.push({
				id: `totals-billing-code:${billingCode.id}`,
				placement: {
					kind: 'at',
					leafId: `billing-code-quantity:${billingCode.id}`,
				},
				rightAligned: true,
				cell: renderDollarValue(
					totals?.billingCodeTotals?.[billingCode.id] ?? 0,
					renderTotalsTooltip(false),
					true,
					false,
					true,
					styles['totals-tooltip-popover--billing-code']
				),
			});
		}

		cells.push(
			{
				id: 'totals-total-material-cost',
				placement: {
					kind: 'at',
					leafId: 'totalMaterialCost',
				},
				rightAligned: true,
				cell: renderDollarValue(
					totals?.totalMaterialCost ?? 0,
					undefined,
					true,
					false,
					true
				),
			},
			{
				id: 'totals-billable-work',
				placement: {
					kind: 'at',
					leafId: 'totalBillableWork',
				},
				rightAligned: true,
				cell: renderDollarValue(
					totals?.totalBillableWork ?? 0,
					undefined,
					true,
					false,
					true
				),
			},
			{
				id: 'totals-revenue',
				placement: {
					kind: 'at',
					leafId: 'totalRevenue',
				},
				rightAligned: true,
				cell: renderDollarValue(
					totals?.totalRevenue ?? 0,
					undefined,
					true
				),
			},
			{
				id: 'totals-job-time',
				placement: {
					kind: 'at',
					leafId: 'totalJobTime',
				},
				rightAligned: true,
				cell: renderTimeTotalsValue(
					totals?.totalJobTime
						? +totals.totalJobTime
						: 0
				),
			},
			{
				id: 'totals-shop-time',
				placement: {
					kind: 'at',
					leafId: 'totalShopTime',
				},
				rightAligned: true,
				cell: renderTimeTotalsValue(
					totals?.totalShopTime
						? +totals.totalShopTime
						: 0
				),
			},
			{
				id: 'totals-travel-time',
				placement: {
					kind: 'at',
					leafId: 'totalTravelTime',
				},
				rightAligned: true,
				cell: renderTimeTotalsValue(
					totals?.totalTravelTime
						? +totals.totalTravelTime
						: 0
				),
			}
		);

		return { cells };
	}, [
		renderDollarValue,
		renderTimeTotalsValue,
		renderTotalsTooltip,
		templateColumns,
		totals?.totalMaterialCost,
		totals?.totalBillableWork,
		totals?.totalRevenue,
		totals?.materialTotals,
		totals?.totalShopTime,
		totals?.totalTravelTime,
		totals?.totalJobTime,
		totals?.billingCodeTotals,
		visibleBillingCodes,
		visibleMaterialHeaders,
	]);

	const materialTotalColumn = React.useMemo<JTColumnDef>(() => {
		return {
			id: 'totalMaterialCost',
			accessor: 'totalMaterialCost',
			header: renderTotalMaterialHeader,
			cell: renderDollarCell(),
			enableSorting: false,
			size: 160,
		};
	}, [renderDollarCell, renderTotalMaterialHeader]);

	React.useEffect(() => {
		fetchTemplate();
	}, [fetchTemplate]);

	React.useEffect(() => {
		checkSyncQueue();
	}, [checkSyncQueue]);

	React.useEffect(() => {
		if (jobHasTemplate && !shouldShowSyncMessage) {
			fetchBillingCodesAndColumns();
		}
	}, [fetchBillingCodesAndColumns, jobHasTemplate, shouldShowSyncMessage]);

	React.useEffect(() => {
		if (jobHasTemplate && !shouldShowSyncMessage) {
			loadFirstPage();
		}
	}, [jobHasTemplate, shouldShowSyncMessage, loadFirstPage]);

	React.useEffect(() => {
		startDate && setLocalStorageStartDate(startDate);
		endDate && setLocalStorageEndDate(endDate);
		period && setLocalStoragePeriod(period);
	}, [startDate, endDate, period]);

	React.useEffect(() => {
		if (!isLoading && !isLoadingMore) {
			checkAndLoad(scrollElementRef.current);
		}
	}, [isLoading, isLoadingMore, checkAndLoad]);

	const allColumns = React.useMemo(() => {
		return [
			...templateColumnsForTable,

			...columnsMaterials,
			materialTotalColumn,

			...columnsBillingCodes,
			billableColumn,

			totalRevenueColumn,

			...timeTotalColumns,
		];
	}, [
		templateColumnsForTable,
		materialTotalColumn,
		columnsMaterials,
		billableColumn,
		columnsBillingCodes,
		totalRevenueColumn,
		timeTotalColumns,
	]);

	const sideDrawerRows = React.useMemo(() => {
		return [
			{
				code: 'Total Billable Work',
				amount: totals?.totalBillableWork ?? 0,
				isMoney: true,
			},
			{
				code: 'Total Material Cost',
				amount: totals?.totalMaterialCost ?? 0,
				isMoney: true,
			},
			{
				code: 'P/R Diff',
				amount: totals?.totalPayrollDifferential ?? 0,
				isMoney: true,
			},
			{
				code: 'Total Ded',
				amount: totals?.totalDeduction ?? 0,
				isMoney: true,
			},
			{
				code: 'MHA',
				amount: totals?.summaryManHourAverage ?? 0,
				isMoney: true,
			},
			{
				code: 'Revenue',
				amount: totals?.summaryRevenue ?? 0,
				isMoney: true,
			},
		];
	}, [totals]);

	const renderSideDrawer = React.useCallback(() => {
		return (
			<div className={styles['job-tracking__side-drawer']}>
				{sideDrawerRows.map((row) => {
					return (
						<div
							className={styles['job-tracking__side-drawer__row']}
							key={row.code}
						>
							<div className={styles['job-tracking__side-drawer__code']}>
								{row.code}
							</div>

							<div className={styles['job-tracking__side-drawer__amount']}>
								{row.isMoney
									? renderDollarValue(Number(row.amount ?? 0), undefined, false, false)
									: <span>{row.amount}</span>}
							</div>
						</div>
					);
				})}
			</div>
		);
	}, [renderDollarValue, sideDrawerRows]);

	if (!hasPermissionsToAccessJobTracking) {
		navigate(CLIENT.ERROR.ERR403());
		return;
	}

	return (
		<div className={styles['table-container']}>
			{
				!jobHasTemplate &&
				<JobTrackingNoTemplate
					jobId={jobId}
					refresh={setTemplateToJob}
				/>
			}
			{
				shouldShowSyncMessage &&
				<JobInSyncQueue
					jobId={jobId}
					refresh={fetchTemplate}
					syncReason={jobSyncQueueStatus.reason}
					syncStatus={jobSyncQueueStatus.status}
				/>
			}
			{jobHasTemplate && !shouldShowSyncMessage &&
				<>
					{renderAdditionalFilter()}
					{(
						isLoading ?
							<div>
								<LoadingIndicator color="orange" />
							</div> :
							<>
								<div onScrollCapture={handleScrollCapture} style={{ overflowY: 'auto', height: '100%' }}>
									<SimpleTableRHFWrapper<JobTrackingRowFM, FormValues, 'rows'>
										columns={allColumns}
										drawerTitle="Summary"
										fields={fields}
										footerRow={totalsRow}
										formAccessorName="rows"
										getCellClassName={getCellClassName}
										height="calc(100vh - 338px)"
										includeDeleteColumn={false}
										key={`${billingCodesDisplay}-${materialsDisplay}`}
										onRowClicked={onRowClicked}
										readonly={true}
										ref={tableRef}
										sideDrawer={renderSideDrawer}
										virtualizeRows
									/>
									{isLoadingMore && <div>Loading...</div>}
								</div>
							</>
					)}
				</>
			}
		</div>
	);
};

function mapDispatchToProps() {
	return {
		findBillingCodesForJobId: BillingCodeActions.findForJobTracking,
		findJobTrackingTemplateColumnsForJobId: JobActions.findJobTrackingTemplateColumns,
		getJobTrackingTable: JobActions.getJobTrackingTable,
		getJobTrackingTotals: JobActions.getJobTrackingTotals,
		exportJobTrackingXlsx: JobActions.exportJobTrackingXlsx,
		getJobTrackingTemplate: JobActions.getJobTrackingTemplate,
		getJobSyncQueue: JobActions.getJobTrackingSyncQueueStatus,
		triggerResync: JobTrackingActions.triggerResync,
	};
}

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;

	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType>(
	React.memo,
	SettingsUtils.withSettings<SettingProps>(() => ([
		{
			key: SettingsKeys.JOB_TRACKING_START_DATE(),
			mappedName: 'startDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.JOB_TRACKING_END_DATE(),
			mappedName: 'endDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.JOB_TRACKING_PERIOD(),
			mappedName: 'period',
			defaultValue: TimePeriodRecurrence.MONTHLY,
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
	])),
	connector
);

export default enhance(JobTracking);
